import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { createApiClient } from "../apiService";
import { addFileToMessageTypes, sendMessageTypes } from "./messageTypes";
import { useToken } from "../tokenUtils";

const domain = getSubdomain();

export const handleGetRequest = async <T>(
  apiClient: any,
  path: string,
  token?: string,
): Promise<T> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  try {
    const response = await apiClient.get(path, { headers });
    return response.data;
  } catch (error: any) {
    console.error(
      "Error en handleGetRequest:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

const handlePutWithQuery = async (
  apiClient: any,
  path: string,
  params: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(path, null, {
    headers,
    params: params,
  });
  return res.data;
};
export const handlePutRequest = async (
  apiClient: any,
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(url, data, { headers });
  return res.data;
};

const handlePutFileRequest = async (
  apiClient: any,
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(url, data, { headers });
  return res.data;
};

const author_article = createApiClient(`${domain}/author/articles/`);

const sendMessageAuthor = async (data: sendMessageTypes): Promise<number> => {
  const token = useToken();
  const res = await handlePutWithQuery(
    author_article,
    `${data.article_slug}/production_message`,
    { message: data.message },
    token,
  );
  return res;
};

const getMessagesAuthor = async (article_slug: string): Promise<any> => {
  const token = useToken();
  try {
    const messages = await handleGetRequest<any>(
      author_article,
      `${article_slug}/production_history`,
      token,
    );
    return messages;
  } catch (error: any) {
    console.error(
      "Error en getMessagesAuthor:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

const addFileToMessageAuthor = async (
  data: addFileToMessageTypes,
): Promise<string> => {
  const token = useToken();
  const path = `${data.article_slug}/production_message/${data.message_id}/file`;
  const res = await handlePutFileRequest(
    author_article,
    path,
    data.file,
    token,
  );
  return res;
};

const editor_article = createApiClient(`${domain}/editor/articles/`);

const sendMessageEditor = async (data: sendMessageTypes): Promise<any> => {
  const token = useToken();
  const res = await handlePutWithQuery(
    editor_article,
    `${data.article_slug}/production_message`,
    { message: data.message },
    token,
  );
  return res;
};

const getMessagesEditor = async (article_slug: string): Promise<any> => {
  const token = useToken();
  try {
    const messages = await handleGetRequest<any>(
      editor_article,
      `${article_slug}/production_history`,
      token,
    );
    return messages;
  } catch (error: any) {
    console.error(
      "Error en getMessagesEditor:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

const addFileToMessageEditor = async (
  data: addFileToMessageTypes,
): Promise<string> => {
  const token = useToken();
  const path = `${data.article_slug}/production_message/${data.message_id}/file`;
  const res = await handlePutFileRequest(
    editor_article,
    path,
    data.file,
    token,
  );
  return res;
};

const editor_volume = createApiClient(`${domain}/editor/volumes/`);

const sendMessageToVol = async (data: {
  volume_slug: string;
  message: string;
}): Promise<any> => {
  const token = useToken();
  const res = await handlePutWithQuery(
    editor_volume,
    `${data.volume_slug}/production_message`,
    { message: data.message },
    token,
  );
  return res;
};

const getVolumeMessages = async (volume_slug: string): Promise<any> => {
  const token = useToken();
  try {
    const messages = await handleGetRequest<any>(
      editor_volume,
      `${volume_slug}/production_history`,
      token,
    );
    return messages;
  } catch (error: any) {
    console.error(
      "Error en getVolumeMessages:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

const getGalleyHistory = async (article_slug: string): Promise<any> => {
  const token = useToken();
  try {
    const messages = await handleGetRequest<any>(
      author_article,
      `${article_slug}/galley_history`,
      token,
    );
    return messages;
  } catch (error: any) {
    console.error(
      "Error en getGalleyHistory:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

const respond_to_galley_author = async ({
  article_slug,
  payload,
}: {
  article_slug: string;
  payload: { status: number; message: string }[];
}): Promise<any> => {
  const token = useToken();
  const res = await handlePutRequest(
    author_article,
    `${article_slug}/galley`,
    payload,
    token,
  );
  return res;
};

const addFileToGalley = async (
  data: addFileToMessageTypes,
): Promise<string> => {
  const token = useToken();
  const path = `${data.article_slug}/galley/${data.message_id}/file`;
  const res = await handlePutFileRequest(
    author_article,
    path,
    data.file,
    token,
  );
  return res;
};
const editor = createApiClient(`${domain}/editor/`);

const respond_to_galley_editor = async ({
  article_slug,
  payload,
}: {
  article_slug: string;
  payload: { status: number; message: string }[];
}): Promise<any> => {
  const token = useToken();
  const res = await handlePutRequest(
    editor,
    `galley/${article_slug}/respond`,
    payload,
    token,
  );
  return res;
};

const addFileToEditorGalley = async (
  data: addFileToMessageTypes,
): Promise<string> => {
  const token = useToken();
  const path = `articles/${data.article_slug}/galley/${data.message_id}/file`;
  const res = await handlePutFileRequest(editor, path, data.file, token);
  return res;
};

const messageService = {
  sendMessageAuthor,
  getMessagesAuthor,
  addFileToMessageAuthor,
  getGalleyHistory,
  respond_to_galley_author,
  respond_to_galley_editor,

  sendMessageEditor,
  getMessagesEditor,
  addFileToMessageEditor,

  sendMessageToVol,
  getVolumeMessages,
  addFileToGalley,
  addFileToEditorGalley,
};

export default messageService;
